import { ChatbotAttachmentProvider } from 'features/chatbot'
import { KnowledgeDocumentTools } from 'features/knowledgeDocumentViewer/components/KnowledgeDocumentTools'
import { DocumentAgentProvider } from 'features/knowledgeDocumentViewer/contexts/DocumentAgentContext'
import { KnowledgeDocumentDataProvider } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import {
  KnowledgeDocumentUXProvider,
  useKnowledgeDocumentUXContext,
} from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import {
  PipelineConfigProvider,
  usePipelineConfig,
} from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { TimeSeriesLinkProvider } from 'features/knowledgeDocumentViewer/contexts/TimeSeriesLinkContext'
import type {
  DocumentHistoryItem,
  DocumentTab,
} from 'features/knowledgeDocumentViewer/types'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import {
  createDefaultSearchHistoryItem,
  createDefaultTab,
  createDocumentTab,
  createTimeSeriesTab,
} from 'features/knowledgeDocumentViewer/utils/tabStateUrlHelpers'
import { GeckoNotificationProvider } from 'features/notifications'
import { FC, useEffect, useMemo, useRef } from 'react'
import { dossiersViewerRouteId } from 'routes/documents/-searchParams'
import styled from 'styled-components'

import { EvidenceDocRelation, EvidenceLinkedDocument } from './types'

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
`

const ToolsWrapper = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;

  /* The claims viewer is much narrower than the full documents page, so make
     the document tab labels smaller and cap their width. */
  [data-slot='tabs-trigger'] {
    font-size: 0.72rem;
    max-width: 12rem;
  }
`

interface ClaimDocumentsInitializerProps {
  documents: EvidenceLinkedDocument[]
}

/**
 * Detects P&ID (piping & instrumentation diagram) documents from their title,
 * drawing number, or role. P&IDs are the most useful sheet for a claim, so we
 * open them first. Matches `P&ID`, `P & ID`, a `PID` token, or the spelled-out
 * form, while avoiding substring false positives like "rapid".
 */
const PID_REGEX =
  /p\s*&\s*id|piping\s*(?:and|&)?\s*instrument|(?:^|[^a-z])pid(?:[^a-z]|$)/i

export const isPidDocument = (doc: EvidenceLinkedDocument): boolean =>
  PID_REGEX.test(`${doc.title ?? ''} ${doc.drawing ?? ''} ${doc.role ?? ''}`)

const isPid = isPidDocument

/**
 * Relations we treat as affirmative "supporting references" worth auto-opening
 * in the viewer. Negative evidence (`excluded`), context (`other`), and manuals
 * (`device_manual`, which also carry no openable `DOC-` id) are left closed —
 * they stay reachable via the dossier's DOC links. Documents with no relation
 * (entries not yet reclassified by the pipeline) keep the prior open-all
 * behavior for backward compatibility.
 */
const AUTO_OPEN_RELATIONS = new Set<EvidenceDocRelation>([
  'establishes',
  'supporting_hardware',
])

/**
 * A real, openable document id is `DOC-` followed by a hex hash. Synthetic refs
 * the dossier mints for non-documents (e.g. `DOC-report-01-IT-LVB211A`, the
 * deterministic historian tag report) are NOT real documents — opening them as
 * a tab just shows an empty "New Tab" the viewer can't load — so they must be
 * excluded from auto-open.
 */
const OPENABLE_DOC_ID = /^DOC-[a-f0-9]{12,}$/i
const isOpenableDocId = (id: string | undefined): id is string =>
  !!id && OPENABLE_DOC_ID.test(id.trim())

const isAutoOpenable = (doc: EvidenceLinkedDocument): boolean =>
  isOpenableDocId(doc.doc_id) &&
  // An affirmative relation the verification gate could not confirm
  // (`relation_verified === false`) is an unverified, over-confidently-worded
  // reference — don't auto-open it as if it backs the tag. (Legacy claims have
  // no `relation_verified`, so they keep the prior behavior.)
  doc.relation_verified !== false &&
  (doc.relation === undefined || AUTO_OPEN_RELATIONS.has(doc.relation))

/**
 * Headless helper rendered inside the document viewer's provider tree. On mount
 * (the panel is keyed by claim, so once per claim) it *replaces* the viewer's
 * open tabs with exactly this claim's referenced documents and focuses the
 * first one, so tabs from a previously selected claim don't accumulate across
 * selections. Renders no UI.
 */
const ClaimDocumentsInitializer: FC<ClaimDocumentsInitializerProps> = ({
  documents,
}) => {
  const { openTabs, activeTabId, setActiveTabId, reorderTabs } =
    useKnowledgeDocumentUXContext()
  const { isPipelineConfigScopeLoading } = usePipelineConfig()

  // Id of the tab to focus once `openTabs` reflects the replacement set below.
  const desiredActiveIdRef = useRef<string | null>(null)
  const appliedActiveRef = useRef(false)
  const hasInitializedRef = useRef(false)

  useEffect(() => {
    // Build one tab per unique referenced document (fall back to the default
    // search tab when the claim references none), then replace the viewer's
    // tabs with that exact set so tabs from a previously selected claim don't
    // linger. Only auto-open affirmative supporting references; skip excluded/
    // other/manual docs (they remain openable from the dossier's DOC links).
    //
    // Wait for the site's pipeline-config scope before minting tabs: FIM
    // evidence doc ids are pipeline-agnostic (bare `DOC-<hash>`), but the viewer
    // resolves system-document entries by their config-scoped id
    // (`DOC-<hash>_<configSuffix>`). Scoping here — a no-op on legacy,
    // config-less sites and on already-scoped ids — makes this embedded viewer's
    // resolution identical to native doc-nav. The ref keeps this to one run per
    // subject (the panel is remounted per subject via React key).
    if (isPipelineConfigScopeLoading || hasInitializedRef.current) return
    hasInitializedRef.current = true

    const seen = new Set<string>()
    const unique = documents.filter((doc) => {
      if (!isAutoOpenable(doc) || !doc.doc_id || seen.has(doc.doc_id)) {
        return false
      }
      seen.add(doc.doc_id)
      return true
    })
    // P&IDs first (best sheet for a claim), everything else after, each group
    // keeping its original order.
    const ordered = [
      ...unique.filter(isPid),
      ...unique.filter((doc) => !isPid(doc)),
    ]
    // Seed each tab's label from the claim's linked-document metadata so tabs
    // read as the document's name immediately, instead of "New Tab" until the
    // user clicks one and its OCR entry loads. Tabs open at the cited page,
    // framed on the cited region when the reference carries geometry.
    const docTabs = ordered.map((doc) => {
      const label = doc.title?.trim() || doc.drawing?.trim()
      const region =
        doc.page && doc.boxes && doc.boxes.length > 0
          ? { pageNum: doc.page, boxes: doc.boxes }
          : undefined
      const tab = createDocumentTab(
        doc.doc_id!,
        doc.page ?? 1,
        [],
        '',
        undefined,
        undefined,
        region
      )
      return label ? { ...tab, customLabel: label } : tab
    })
    const tabs = docTabs.length > 0 ? docTabs : [createDefaultTab()]
    reorderTabs(tabs)
    desiredActiveIdRef.current = tabs[0].id
    appliedActiveRef.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPipelineConfigScopeLoading])

  // `setActiveTabId` resolves against the current `openTabs`, so focus the
  // first replacement tab only once the reorder above has been applied.
  useEffect(() => {
    const desired = desiredActiveIdRef.current
    if (!desired || appliedActiveRef.current) return
    if (openTabs.some((tab) => tab.id === desired)) {
      appliedActiveRef.current = true
      if (activeTabId !== desired) setActiveTabId(desired)
    }
  }, [openTabs, activeTabId, setActiveTabId])

  return null
}

/** A request to open a specific document tab, bumped per click so repeat
 * clicks on the same doc re-trigger the effect. */
export interface OpenDocRequest {
  docId: string
  nonce: number
  /** Optional 1-based page to open the document at. */
  page?: number
  /** Optional text fragment to highlight on that page (e.g. an anchor id). */
  fragmentText?: string
  /**
   * Cited region rects on `page` (normalized fractions). Takes precedence
   * over `fragmentText` — geometry needs no server round-trip and is exact.
   */
  boxes?: NormalizedBBox[]
}

interface ClaimDocumentOpenerProps {
  request: OpenDocRequest | null
}

/**
 * Headless helper inside the viewer's provider tree. When the parent issues an
 * open-document request (from a `DOC-…` link in the claim dossier) it focuses
 * the matching tab if already open, otherwise opens a new document tab.
 */
const ClaimDocumentOpener: FC<ClaimDocumentOpenerProps> = ({ request }) => {
  const { openTabs, addDocumentTab, setActiveTabId, pushToHistory } =
    useKnowledgeDocumentUXContext()

  const openTabsRef = useRef(openTabs)
  openTabsRef.current = openTabs
  const lastNonceRef = useRef<number | null>(null)

  useEffect(() => {
    if (!request || lastNonceRef.current === request.nonce) return
    lastNonceRef.current = request.nonce

    const { page, fragmentText, boxes } = request
    const docId = request.docId
    // Geometry beats text: an exact cited region needs no server search.
    const regionHighlight =
      page && boxes && boxes.length > 0 ? { pageNum: page, boxes } : undefined
    const fragmentHighlight =
      !regionHighlight && page && fragmentText
        ? { pageNum: page, fragmentText }
        : undefined

    const existing = openTabsRef.current.find((tab) => {
      const item = tab.history[tab.currentIndex]
      return item?.type === 'document' && item.documentId === docId
    })
    if (existing) {
      setActiveTabId(existing.id)
      // When a page (and optional highlight) is requested, navigate the already
      // open tab to it rather than just refocusing the tab.
      if (page) {
        const item: DocumentHistoryItem = {
          type: 'document',
          documentId: docId,
          pageNumber: page,
          selectedBoxIds: [],
          query: '',
          ...(fragmentHighlight && { fragmentHighlight }),
          ...(regionHighlight && { regionHighlight }),
        }
        pushToHistory(existing.id, item)
      }
    } else {
      addDocumentTab({
        documentId: docId,
        ...(page && { pageNumber: page }),
        ...(fragmentHighlight && { fragmentHighlight }),
        ...(regionHighlight && { regionHighlight }),
      })
    }
  }, [request, addDocumentTab, setActiveTabId, pushToHistory])

  return null
}

/** A request to open a new search tab for a term, bumped per click. */
export interface SearchDocRequest {
  term: string
  nonce: number
}

interface ClaimDocumentSearcherProps {
  request: SearchDocRequest | null
}

/**
 * Headless helper inside the viewer's provider tree. When the parent issues a
 * search request (from a manual/spec reference in the dossier that has no
 * openable `DOC-` id) it opens a fresh search tab pre-filled with that term, so
 * the user lands on the document search for it.
 */
const ClaimDocumentSearcher: FC<ClaimDocumentSearcherProps> = ({ request }) => {
  const { addTab } = useKnowledgeDocumentUXContext()
  const lastNonceRef = useRef<number | null>(null)

  useEffect(() => {
    if (!request || lastNonceRef.current === request.nonce) return
    lastNonceRef.current = request.nonce

    const base = createDefaultTab()
    const tab: DocumentTab = {
      ...base,
      history: [
        {
          ...createDefaultSearchHistoryItem(),
          searchState: {
            ...createDefaultSearchHistoryItem().searchState,
            query: request.term,
          },
        },
      ],
      currentIndex: 0,
    }
    // addTab appends the tab and focuses it; the search runs off the seeded
    // query automatically.
    addTab(tab)
  }, [request, addTab])

  return null
}

const PROCESS_DATA_LABEL = 'Process Data'
const PROCESS_WINDOW_DAYS = 30

const isProcessDataTab = (tab: DocumentTab): boolean =>
  tab.closable === false && tab.history[tab.currentIndex]?.type === 'timeseries'

interface EvidenceProcessDataTabSyncProps {
  /** Controlled sensor set for the pinned process-data tab. */
  sensorIds: string[]
  sensorColors?: Record<string, string>
  /** Related pens rendered de-emphasized in the viewer chart. */
  deemphasizedSensorIds?: string[]
  /** Focus Process Data when it is first created. Linked documents take precedence. */
  focusOnCreate: boolean
}

/**
 * Keeps a subject's process data in the document viewer, rather than splitting
 * the right-hand pane between a viewer and a separate chart. The tab is pinned
 * left-most and updated in place as the subject detail toggles streams on/off.
 */
const EvidenceProcessDataTabSync: FC<EvidenceProcessDataTabSyncProps> = ({
  sensorIds,
  sensorColors,
  deemphasizedSensorIds,
  focusOnCreate,
}) => {
  const {
    openTabs,
    removeTab,
    setActiveTabId,
    setTimeSeriesTabSensors,
    prependPinnedTimeSeriesTab,
    moveTabToFront,
  } = useKnowledgeDocumentUXContext()

  // Compute once per selected subject (the parent panel is keyed by subject).
  const timeWindow = useMemo(() => {
    const to = new Date()
    const from = new Date(to.getTime() - PROCESS_WINDOW_DAYS * 86400_000)
    return { from: from.toISOString(), to: to.toISOString() }
  }, [])
  const pendingFocusRef = useRef<string | null>(null)

  useEffect(() => {
    const existing = openTabs.find(isProcessDataTab)

    if (sensorIds.length === 0) {
      if (existing) removeTab(existing.id)
      return
    }

    if (!existing) {
      const tab = createTimeSeriesTab(
        sensorIds,
        PROCESS_DATA_LABEL,
        timeWindow,
        false,
        {
          controlled: true,
          closable: false,
          sensorColors,
          deemphasizedSensorIds,
        }
      )
      // Functional prepend avoids clobbering document tabs seeded by the
      // sibling initializer in the same render pass.
      prependPinnedTimeSeriesTab(tab)
      pendingFocusRef.current = focusOnCreate ? tab.id : null
      return
    }

    moveTabToFront(existing.id)
    setTimeSeriesTabSensors(existing.id, sensorIds, {
      sensorColors: sensorColors ?? {},
      deemphasizedSensorIds: deemphasizedSensorIds ?? [],
    })

    if (pendingFocusRef.current === existing.id) {
      pendingFocusRef.current = null
      setActiveTabId(existing.id)
    }
  }, [
    openTabs,
    sensorIds,
    sensorColors,
    deemphasizedSensorIds,
    focusOnCreate,
    timeWindow,
    removeTab,
    prependPinnedTimeSeriesTab,
    moveTabToFront,
    setActiveTabId,
    setTimeSeriesTabSensors,
  ])

  return null
}

interface ClaimDocumentsPanelProps {
  siteId: string
  organizationId: string
  documents: EvidenceLinkedDocument[]
  /** Open-document request driven by `DOC-…` links in the claim dossier. */
  openDocRequest: OpenDocRequest | null
  /** Search request driven by manual/spec references with no `DOC-` id. */
  searchDocRequest: SearchDocRequest | null
  /** Controlled sensor set for the optional pinned Process Data tab. */
  processSensorIds?: string[]
  processSensorColors?: Record<string, string>
  processDeemphasizedSensorIds?: string[]
}

/**
 * Embeds the full knowledge document viewer (real OCR tabs, search, sidebar)
 * scoped to the Claims page, with a claim-specific bar listing the referenced
 * documents. Mirrors the provider stack in
 * {@link KnowledgeDocumentViewer} but injects the referenced-documents bar as a
 * child so it can drive the viewer's tab state.
 */
export const EvidenceDocumentsPanel: FC<ClaimDocumentsPanelProps> = ({
  siteId,
  organizationId,
  documents,
  openDocRequest,
  searchDocRequest,
  processSensorIds,
  processSensorColors,
  processDeemphasizedSensorIds,
}) => {
  const hasAutoOpenableDocument = documents.some(isAutoOpenable)

  return (
    <Panel>
      <GeckoNotificationProvider>
        <ChatbotAttachmentProvider>
          <PipelineConfigProvider siteID={siteId}>
            <KnowledgeDocumentUXProvider
              basePath={dossiersViewerRouteId}
              viewOnly
              siteId={siteId}>
              <KnowledgeDocumentDataProvider
                siteID={siteId}
                orgID={organizationId}>
                <DocumentAgentProvider>
                  <TimeSeriesLinkProvider siteID={siteId}>
                    <ClaimDocumentsInitializer documents={documents} />
                    <ClaimDocumentOpener request={openDocRequest} />
                    <ClaimDocumentSearcher request={searchDocRequest} />
                    {processSensorIds && (
                      <EvidenceProcessDataTabSync
                        sensorIds={processSensorIds}
                        sensorColors={processSensorColors}
                        deemphasizedSensorIds={processDeemphasizedSensorIds}
                        focusOnCreate={!hasAutoOpenableDocument}
                      />
                    )}
                    <ToolsWrapper>
                      <KnowledgeDocumentTools scale={2} />
                    </ToolsWrapper>
                  </TimeSeriesLinkProvider>
                </DocumentAgentProvider>
              </KnowledgeDocumentDataProvider>
            </KnowledgeDocumentUXProvider>
          </PipelineConfigProvider>
        </ChatbotAttachmentProvider>
      </GeckoNotificationProvider>
    </Panel>
  )
}
